import {
    cloneChildNodes,
    dispatchValueEvents,
    getStringAttribute,
    hasScrollableRange,
    isNearScrollEnd,
    normalizeSearchText,
    reconcilePickerPanel,
    resolveSearchResultsScrollHost,
    resolveScopedUrl,
    searchResultsAreNearEnd,
    setStringAttribute,
    scrollMetrics,
    upgradeProperty,
} from "./searchable-picker-core.js";

const CLOSE_DELAY_MS = 150;
const RESULTS_PAGE_SIZE = 24;
const SEARCH_QUERY_PARAM = "q";
const OFFSET_QUERY_PARAM = "offset";
const SELECTED_QUERY_PARAM = "selected";
const MAX_SELECTION_PAYLOAD_BYTES = 4096;
export const SEARCHABLE_DROPDOWN_OPEN_EVENT = "fishystuff:searchable-dropdown-open";
export const SEARCHABLE_DROPDOWN_CLOSE_EVENT = "fishystuff:searchable-dropdown-close";
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const CUSTOM_TRADE_DISTANCE_IDENTITY_PATTERN = /^custom:(\d+(?:\.\d+)?|\.\d+)$/;
const HTMLElementBase = globalThis.HTMLElement ?? class {};
export function normalizeIsoDateValue(value) {
    const normalized = String(value ?? "").trim();
    if (!ISO_DATE_PATTERN.test(normalized)) {
        return "";
    }
    const [yearRaw, monthRaw, dayRaw] = normalized.split("-");
    const year = Number.parseInt(yearRaw, 10);
    const month = Number.parseInt(monthRaw, 10);
    const day = Number.parseInt(dayRaw, 10);
    if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
        return "";
    }
    const date = new Date(Date.UTC(year, month - 1, day));
    if (Number.isNaN(date.getTime())) {
        return "";
    }
    if (
        date.getUTCFullYear() !== year
        || date.getUTCMonth() !== month - 1
        || date.getUTCDate() !== day
    ) {
        return "";
    }
    return normalized;
}

export function normalizeTradeDistanceBonusCustomIdentity(value) {
    const raw = String(value ?? "").trim();
    const match = raw.match(CUSTOM_TRADE_DISTANCE_IDENTITY_PATTERN);
    if (!match) {
        return null;
    }
    const number = Number(match[1]);
    if (!Number.isFinite(number) || number < 0) {
        return null;
    }
    const rounded = Math.round(number * 10) / 10;
    const fixed = rounded.toFixed(1);
    return {
        value: `custom:${fixed.replace(/\.0$/, "")}`,
        label: fixed,
        displayText: `${fixed}%`,
    };
}

function parseHtmlFragment(html) {
    const template = document.createElement("template");
    template.innerHTML = String(html ?? "");
    return template.content;
}

function parseSelectionPayload(option) {
    if (!(option instanceof HTMLElement) || !option.hasAttribute("data-selection-payload")) {
        return null;
    }
    const encoded = String(option.getAttribute("data-selection-payload") ?? "");
    if (new TextEncoder().encode(encoded).byteLength > MAX_SELECTION_PAYLOAD_BYTES) {
        throw new TypeError("searchable dropdown selection payload is too large");
    }
    const payload = JSON.parse(encoded);
    if (payload === null || typeof payload !== "object" || Array.isArray(payload)) {
        throw new TypeError("searchable dropdown selection payload must be an object");
    }
    return payload;
}

export class FishySearchableDropdown extends HTMLElementBase {
    static get observedAttributes() {
        return [
            "label",
            "placeholder",
            "search-url",
            "search-url-root",
            "value",
            "exclude-selected-inputs",
        ];
    }

    constructor() {
        super();
        this._closeTimer = 0;
        this._lastSearchKey = "";
        this._outsideListenerAttached = false;
        this._viewportListenersAttached = false;
        this._panelAnchor = null;
        this._panelDetached = false;
        this._panelElement = null;
        this._panelEventsAttached = false;
        this._panelPositionFrame = 0;
        this._autoFilledSearchKey = "";
        this._pendingAutoFillSearchKey = "";
        this._resultsElement = null;
        this._resultsMorphObserver = null;
        this._resultsRecoveryQueued = false;
        this._resultsScrollHost = null;
        this._activeSearchMode = "";
        this._activeSearchQuery = "";
        this._activeSearchSelectedValue = "";
        this._loadMoreController = null;
        this._localSearchState = null;
        this._searchController = null;
        this._selectionHydrationController = null;
        this._selectionPayload = null;
        this._preparedValuePresentation = null;
        this._selectedPresentationCache = new Map();

        this._handleClick = this._handleClick.bind(this);
        this._handleDocumentPointerDown = this._handleDocumentPointerDown.bind(this);
        this._handleFocusIn = this._handleFocusIn.bind(this);
        this._handleFocusOut = this._handleFocusOut.bind(this);
        this._handleInput = this._handleInput.bind(this);
        this._handleKeyDown = this._handleKeyDown.bind(this);
        this._handleMouseDown = this._handleMouseDown.bind(this);
        this._handleResultsScroll = this._handleResultsScroll.bind(this);
        this._handleViewportChange = this._handleViewportChange.bind(this);

        this.addEventListener("click", this._handleClick);
        this.addEventListener("focusin", this._handleFocusIn);
        this.addEventListener("focusout", this._handleFocusOut);
        this.addEventListener("input", this._handleInput);
        this.addEventListener("keydown", this._handleKeyDown);
        this.addEventListener("mousedown", this._handleMouseDown);
    }

    get label() {
        return getStringAttribute(this, "label");
    }

    set label(value) {
        setStringAttribute(this, "label", value);
    }

    get placeholder() {
        return getStringAttribute(this, "placeholder");
    }

    set placeholder(value) {
        setStringAttribute(this, "placeholder", value);
    }

    get searchUrl() {
        return getStringAttribute(this, "search-url");
    }

    set searchUrl(value) {
        setStringAttribute(this, "search-url", value);
    }

    get searchUrlRoot() {
        return getStringAttribute(this, "search-url-root");
    }

    set searchUrlRoot(value) {
        setStringAttribute(this, "search-url-root", value);
    }

    get value() {
        return getStringAttribute(this, "value");
    }

    set value(value) {
        setStringAttribute(this, "value", value);
    }

    get selectionPayload() {
        return this._selectionPayload === null
            ? null
            : JSON.parse(JSON.stringify(this._selectionPayload));
    }

    get excludeSelectedInputs() {
        return getStringAttribute(this, "exclude-selected-inputs");
    }

    set excludeSelectedInputs(value) {
        setStringAttribute(this, "exclude-selected-inputs", value);
    }

    connectedCallback() {
        upgradeProperty(this, "label");
        upgradeProperty(this, "placeholder");
        upgradeProperty(this, "searchUrl");
        upgradeProperty(this, "searchUrlRoot");
        upgradeProperty(this, "value");
        upgradeProperty(this, "excludeSelectedInputs");
        this._ensurePanelReference();
        this._attachResultsEvents();
        this._observeResultsMorphs();

        queueMicrotask(() => {
            if (!this.isConnected) {
                return;
            }
            this._syncUi();
            this._syncFromValue(this.value);
            this._rememberSelectedPresentation(this.value);
            this._hydrateRenderedRemoteResults();
        });
    }

    disconnectedCallback() {
        this.close();
        this._detachPanelEvents();
        this._detachResultsEvents();
        this._disconnectResultsMorphObserver();
        this._cancelClose();
        this._abortSearch();
        this._abortLoadMore();
        this._abortSelectionHydration();
        this._preparedValuePresentation = null;
        this._detachOutsideListener();
        this._detachViewportListeners();
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (oldValue === newValue) {
            return;
        }
        if (name === "value") {
            this._selectionPayload = null;
            this._lastSearchKey = "";
            this._syncFromValue(newValue, {
                resolveMissingRemote: this.isConnected,
                valueChanged: true,
            });
        }
        if (name === "search-url" || name === "search-url-root") {
            this._lastSearchKey = "";
            if (this.isOpen()) {
                this.search(this.searchInputElement()?.value ?? "");
            }
        }
        this._syncUi();
    }

    open() {
        if (this.triggerElement()?.getAttribute("aria-disabled") === "true") {
            return;
        }
        const panel = this.panelElement();
        const input = this.searchInputElement();
        if (!(panel instanceof HTMLElement) || !(input instanceof HTMLInputElement)) {
            return;
        }
        const wasOpen = this.isOpen();

        this._cancelClose();
        panel.hidden = false;
        this.style.zIndex = "60";
        this._setExpanded(true);
        this._attachOutsideListener();
        let detachedPanelWidth = 0;
        if (this._usesDetachedPanel()) {
            detachedPanelWidth = this._measurePanelRect().width;
            this._detachPanel();
            this._attachPanelEvents();
            this._attachViewportListeners();
            this._positionPanel(detachedPanelWidth);
        }
        if (!wasOpen) {
            this.dispatchEvent(new CustomEvent(SEARCHABLE_DROPDOWN_OPEN_EVENT, {
                bubbles: true,
                detail: { dropdown: this },
            }));
        }
        input.value = "";

        window.requestAnimationFrame(() => {
            if (!this.isConnected || !this.isOpen()) {
                return;
            }
            if (this._usesDetachedPanel()) {
                this._positionPanel(detachedPanelWidth);
            }
            input.focus();
            this.search("");
            this._centerSelectedResult(
                this.resultsElement(),
                this.value,
            );
        });
    }

    close() {
        this._cancelClose();

        const panel = this.panelElement();
        const wasOpen = this.isOpen();
        if (wasOpen && panel.contains(document.activeElement)) {
            this.triggerElement()?.focus({ preventScroll: true });
        }
        if (panel instanceof HTMLElement) {
            panel.hidden = true;
        }

        this.style.zIndex = "";
        this._abortSearch();
        this._abortLoadMore();
        this._detachOutsideListener();
        if (this._usesDetachedPanel()) {
            this._detachViewportListeners();
        }
        this._setExpanded(false);
        if (this._usesDetachedPanel()) {
            this._restorePanel();
        }
        if (wasOpen) {
            this.dispatchEvent(new CustomEvent(SEARCHABLE_DROPDOWN_CLOSE_EVENT, {
                bubbles: true,
                detail: { dropdown: this },
            }));
        }
    }

    refreshResults() {
        this._lastSearchKey = "";
        this.search(this.searchInputElement()?.value ?? "");
    }

    _observeResultsMorphs() {
        if (this._resultsMorphObserver || typeof MutationObserver !== "function") {
            return;
        }
        this._resultsMorphObserver = new MutationObserver(
            () => this._scheduleEmptiedResultsRecovery(),
        );
        this._observeResultsMorphTargets();
    }

    _observeResultsMorphTargets() {
        const observer = this._resultsMorphObserver;
        if (!observer) {
            return;
        }
        observer.disconnect();
        observer.observe(this, { childList: true, subtree: true });
        const panel = this._panelElement;
        if (panel instanceof HTMLElement && panel !== this) {
            observer.observe(panel, { childList: true, subtree: true });
        }
    }

    _disconnectResultsMorphObserver() {
        this._resultsMorphObserver?.disconnect?.();
        this._resultsMorphObserver = null;
        this._resultsRecoveryQueued = false;
    }

    _scheduleEmptiedResultsRecovery() {
        if (this._resultsRecoveryQueued) {
            return;
        }
        this._resultsRecoveryQueued = true;
        queueMicrotask(() => {
            this._resultsRecoveryQueued = false;
            this._recoverEmptiedRemoteResults();
        });
    }

    _recoverEmptiedRemoteResults() {
        const results = this.resultsElement();
        if (
            !this.isConnected
            || !this.isOpen()
            || !this.searchUrl
            || this._searchController
            || !(results instanceof HTMLElement)
            || results.firstElementChild
        ) {
            return false;
        }
        this.refreshResults();
        return true;
    }

    toggle() {
        if (this.isOpen()) {
            this.close();
            return;
        }
        this.open();
    }

    search(rawQuery) {
        const query = String(rawQuery ?? "");
        const results = this.resultsElement();
        if (!(results instanceof HTMLElement)) {
            return;
        }

        const selectedValue = this.value;
        const excludedValues = this._excludedSelectedInputValues(selectedValue);
        const searchKey = this._buildSearchKey(query, selectedValue, excludedValues);
        // Datastar may retain this custom element while morphing a dependent
        // control's result list back to its empty server shell. A matching
        // request key only describes usable cached results while that shell
        // still contains a rendered response. Server-rendered empty shells can
        // retain formatting whitespace without containing a result element.
        if (this._lastSearchKey === searchKey && results.firstElementChild) {
            return;
        }

        this._cancelClose();
        this._lastSearchKey = searchKey;
        this._autoFilledSearchKey = "";
        this._pendingAutoFillSearchKey = "";
        this._abortSearch();
        this._abortLoadMore();
        this._setResultsNextOffset(results, null);
        results.removeAttribute("aria-busy");
        this._activeSearchQuery = query;
        this._activeSearchSelectedValue = selectedValue;
        this._localSearchState = null;

        const searchUrl = this._buildSearchUrl(query);
        if (!searchUrl) {
            this._activeSearchMode = "local";
            this._renderLocalResults(query, selectedValue, excludedValues);
            return;
        }

        this._activeSearchMode = "remote";
        const controller = new AbortController();
        this._searchController = controller;
        fetch(searchUrl, {
            cache: "no-store",
            headers: {
                Accept: "text/html",
            },
            signal: controller.signal,
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Search request failed: ${response.status}`);
                }
                return response.text();
            })
            .then((html) => {
                if (controller.signal.aborted) {
                    return;
                }

                const currentResults = this.resultsElement();
                if (!(currentResults instanceof HTMLElement)) {
                    return;
                }

                const fragment = parseHtmlFragment(html);
                const nextResults = fragment.firstElementChild;
                if (!(nextResults instanceof HTMLElement)) {
                    return;
                }
                this._filterExcludedRemoteOptions(nextResults, selectedValue);
                currentResults.replaceWith(nextResults);
                this._attachResultsEvents();
                this._syncFromValue(selectedValue);
                this._syncResultsPagination();
                this._positionOpenDetachedPanel();
                if (!query.trim()) {
                    this._centerSelectedResult(nextResults, selectedValue);
                }
            })
            .catch((error) => {
                if (error?.name === "AbortError") {
                    return;
                }
                console.error("Error loading searchable dropdown results:", error);
            })
            .finally(() => {
                if (this._searchController === controller) {
                    this._searchController = null;
                    this._scheduleAutoFillIfNeeded();
                }
            });
    }

    select(value, label, selectionPayload = null) {
        this.value = value;
        this.label = label;
        this._selectionPayload = selectionPayload === null
            ? null
            : JSON.parse(JSON.stringify(selectionPayload));

        const input = this.searchInputElement();
        if (input instanceof HTMLInputElement) {
            input.value = "";
        }

        this._lastSearchKey = "";
        dispatchValueEvents(this);
        this.close();
    }

    isOpen() {
        const panel = this.panelElement();
        return panel instanceof HTMLElement ? !panel.hidden : false;
    }

    panelElement() {
        this._ensurePanelReference();
        return this._panelElement;
    }

    resultsElement() {
        return this.panelElement()?.querySelector?.('[data-role="results"]') ?? null;
    }

    searchInputElement() {
        return this.panelElement()?.querySelector?.('[data-role="search-input"]') ?? null;
    }

    selectedLabelElement() {
        return this.querySelector('[data-role="selected-label"]');
    }

    selectedContentElement() {
        const content = this.querySelector('[data-role="selected-content"]');
        return content instanceof HTMLTemplateElement ? null : content;
    }

    selectedContentCatalogElement() {
        return this.querySelector('[data-role="selected-content-catalog"]');
    }

    triggerElement() {
        return this.querySelector('[data-role="trigger"]');
    }

    _buildCustomOption(rawQuery, selectedValue, templates) {
        const mode = getStringAttribute(this, "custom-option-mode");
        if (mode !== "iso-date") {
            return null;
        }
        const queryValue = normalizeIsoDateValue(rawQuery);
        const selectedDateValue = normalizeIsoDateValue(selectedValue);
        const customValue = queryValue || (!String(rawQuery ?? "").trim() ? selectedDateValue : "");
        if (!customValue) {
            return null;
        }
        const templateValues = new Set(
            (Array.isArray(templates) ? templates : [])
                .map((template) => String(template.getAttribute("data-value") ?? "").trim())
                .filter(Boolean),
        );
        if (templateValues.has(customValue)) {
            return null;
        }

        const item = document.createElement("li");
        const button = document.createElement("button");
        button.type = "button";
        button.className = `justify-between gap-3 text-left${customValue === selectedValue ? " menu-active" : ""}`;
        button.dataset.searchableDropdownOption = "";
        button.setAttribute("data-value", customValue);
        button.setAttribute("data-label", customValue);

        const optionContent = document.createElement("span");
        optionContent.dataset.role = "option-content";
        optionContent.className = "flex min-w-0 flex-1 items-center gap-3";

        const label = document.createElement("span");
        label.className = "truncate font-medium";
        label.textContent = `Use date ${customValue}`;
        optionContent.append(label);
        button.append(optionContent);

        const selectedTemplate = document.createElement("template");
        selectedTemplate.dataset.role = "selected-content";
        selectedTemplate.innerHTML = `<span class="truncate font-medium">${customValue}</span>`;
        button.append(selectedTemplate);

        if (customValue === selectedValue) {
            const badge = document.createElement("span");
            badge.className = "badge badge-soft badge-primary badge-xs";
            badge.textContent = "Selected";
            button.append(badge);
        }

        item.append(button);
        return item;
    }

    _tradeDistanceBonusCustomLabels(custom) {
        const label = getStringAttribute(this, "custom-option-label") || "Custom distance";
        const description = getStringAttribute(this, "custom-option-description") || "Manual distance bonus";
        return {
            label,
            description,
            badge: custom?.displayText ?? "",
        };
    }

    _buildTradeDistanceBonusCustomContent(custom) {
        const labels = this._tradeDistanceBonusCustomLabels(custom);
        const root = document.createElement("span");
        root.className = "min-w-0 flex-1";

        const title = document.createElement("span");
        title.className = "block truncate font-medium text-base-content";
        title.textContent = labels.label;
        root.append(title);

        const meta = document.createElement("span");
        meta.className = "mt-1 flex flex-wrap items-center gap-2 text-xs text-base-content/65";

        const description = document.createElement("span");
        description.className = "truncate";
        description.textContent = labels.description;
        meta.append(description);

        const badge = document.createElement("span");
        badge.className = "badge badge-sm badge-soft badge-warning";
        badge.textContent = labels.badge;
        meta.append(badge);

        root.append(meta);
        return root;
    }

    _attachPanelEvents() {
        const panel = this.panelElement();
        if (!(panel instanceof HTMLElement) || this._panelEventsAttached) {
            return;
        }
        panel.addEventListener("click", this._handleClick);
        panel.addEventListener("focusin", this._handleFocusIn);
        panel.addEventListener("focusout", this._handleFocusOut);
        panel.addEventListener("input", this._handleInput);
        panel.addEventListener("keydown", this._handleKeyDown);
        panel.addEventListener("mousedown", this._handleMouseDown);
        this._panelEventsAttached = true;
    }

    _attachResultsEvents() {
        const results = this.resultsElement();
        const scrollHost = this._resolveResultsScrollHost(results);
        if (this._resultsElement === results && this._resultsScrollHost === scrollHost) {
            return;
        }
        this._detachResultsEvents();
        if (!(results instanceof HTMLElement) || !(scrollHost instanceof HTMLElement)) {
            return;
        }
        scrollHost.addEventListener("scroll", this._handleResultsScroll, { passive: true });
        this._resultsElement = results;
        this._resultsScrollHost = scrollHost;
    }

    _resolveResultsScrollHost(results = this.resultsElement()) {
        if (!(results instanceof HTMLElement)) {
            return null;
        }
        return resolveSearchResultsScrollHost(results, this.panelElement());
    }

    _attachViewportListeners() {
        if (this._viewportListenersAttached) {
            return;
        }
        window.addEventListener("resize", this._handleViewportChange);
        if (!this._usesOverlayAnchorPlacement()) {
            window.addEventListener("scroll", this._handleViewportChange, true);
        }
        window.visualViewport?.addEventListener?.("resize", this._handleViewportChange);
        window.visualViewport?.addEventListener?.("scroll", this._handleViewportChange);
        this._viewportListenersAttached = true;
    }

    _abortSearch() {
        if (!this._searchController) {
            return;
        }
        this._searchController.abort();
        this._searchController = null;
    }

    _abortLoadMore() {
        if (!this._loadMoreController) {
            return;
        }
        this._loadMoreController.abort();
        this._loadMoreController = null;
    }

    _abortSelectionHydration() {
        if (!this._selectionHydrationController) {
            return;
        }
        this._selectionHydrationController.abort();
        this._selectionHydrationController = null;
    }

    _attachOutsideListener() {
        if (this._outsideListenerAttached) {
            return;
        }
        document.addEventListener("pointerdown", this._handleDocumentPointerDown, true);
        this._outsideListenerAttached = true;
    }

    _clearDetachedPanelStyles() {
        const panel = this.panelElement();
        if (!(panel instanceof HTMLElement)) {
            return;
        }
        panel.style.position = "";
        panel.style.left = "";
        panel.style.top = "";
        panel.style.width = "";
        panel.style.minWidth = "";
        panel.style.maxWidth = "";
        panel.style.maxHeight = "";
        panel.style.overscrollBehavior = "";
        panel.style.zIndex = "";
        panel.style.margin = "";
        this._clearDetachedResultsStyles();
    }

    _clearDetachedResultsStyles() {
        const results = this.resultsElement();
        if (!(results instanceof HTMLElement)) {
            return;
        }
        results.style.maxHeight = "";
        results.style.overflowY = "";
        results.style.overscrollBehavior = "";
    }

    _configuredPanelWidthStyle() {
        const width = getStringAttribute(this, "panel-width");
        if (!width) {
            return "";
        }
        if (width === "max-content") return width;
        const edgeInset = this._usesOverlayAnchorPlacement() ? "0px" : "24px";
        return `min(${width}, calc(100vw - ${edgeInset}))`;
    }

    _buildSearchUrl(query, offset = null, selectedValue = this.value, searchUrl = "") {
        const resolved = searchUrl || resolveScopedUrl(this.searchUrl, this.searchUrlRoot);
        if (!resolved) {
            return "";
        }

        let url;
        try {
            url = new URL(resolved, window.location.href);
        } catch (_) {
            return "";
        }

        const normalizedQuery = String(query ?? "").trim();
        const normalizedSelectedValue = String(selectedValue ?? "");

        if (normalizedQuery) {
            url.searchParams.set(SEARCH_QUERY_PARAM, normalizedQuery);
        } else {
            url.searchParams.delete(SEARCH_QUERY_PARAM);
        }

        if (normalizedSelectedValue) {
            url.searchParams.set(SELECTED_QUERY_PARAM, normalizedSelectedValue);
        } else {
            url.searchParams.delete(SELECTED_QUERY_PARAM);
        }

        if (Number.isInteger(offset) && offset > 0) {
            url.searchParams.set(OFFSET_QUERY_PARAM, String(offset));
        } else {
            url.searchParams.delete(OFFSET_QUERY_PARAM);
        }

        const calculatorOwner = this.closest?.("[data-calculator-source-reference]");
        const sourceReference = String(
            calculatorOwner?.getAttribute?.("data-calculator-source-reference") ?? "",
        ).trim();
        if (sourceReference) {
            url.searchParams.set("ref", sourceReference);
        }

        return url.toString();
    }

    async prepareValuePresentation(rawValue, { searchUrl = "" } = {}) {
        const value = String(rawValue ?? "");
        const hasCallerContext = Boolean(String(searchUrl ?? "").trim());
        if (!hasCallerContext && this._canResolveValuePresentation(value)) {
            return;
        }

        this._abortSelectionHydration();
        if (hasCallerContext) {
            this._preparedValuePresentation = null;
        }
        const resolvedSearchUrl = this._buildSearchUrl("", null, value, searchUrl);
        if (!resolvedSearchUrl) {
            throw new Error(`Selected option has no presentation source: ${value}`);
        }

        const controller = new AbortController();
        this._selectionHydrationController = controller;
        try {
            const response = await fetch(resolvedSearchUrl, {
                cache: "no-store",
                headers: { Accept: "text/html" },
                signal: controller.signal,
            });
            if (!response.ok) {
                throw new Error(`Selected option request failed: ${response.status}`);
            }
            const fragment = parseHtmlFragment(await response.text());
            if (controller.signal.aborted) {
                return;
            }
            const option = Array.from(
                fragment.querySelectorAll?.("[data-searchable-dropdown-option]") ?? [],
            ).find((candidate) => candidate.getAttribute("data-value") === value) ?? null;
            if (!(option instanceof HTMLElement)) {
                throw new Error(`Selected option is unavailable: ${value}`);
            }
            const presentation = this._rememberOptionPresentation(value, option);
            if (hasCallerContext) {
                this._preparedValuePresentation = {
                    value,
                    presentation,
                };
            }
            if (this.value === value) {
                this._syncFromValue(value);
            }
        } finally {
            if (this._selectionHydrationController === controller) {
                this._selectionHydrationController = null;
            }
        }
    }

    _cancelClose() {
        if (!this._closeTimer) {
            return;
        }
        window.clearTimeout(this._closeTimer);
        this._closeTimer = 0;
    }

    _detachOutsideListener() {
        if (!this._outsideListenerAttached) {
            return;
        }
        document.removeEventListener("pointerdown", this._handleDocumentPointerDown, true);
        this._outsideListenerAttached = false;
    }

    _detachPanel(panel = this.panelElement()) {
        const host = this.closest("dialog[open]") || document.body || document.documentElement;
        if (!(panel instanceof HTMLElement) || !(host instanceof HTMLElement) || panel.parentNode === host) {
            return;
        }
        if (!(this._panelAnchor instanceof Node) && panel.parentNode) {
            this._panelAnchor = document.createComment("fishy-searchable-dropdown-panel");
            panel.parentNode.insertBefore(this._panelAnchor, panel);
        }
        const focused = panel.contains(document.activeElement) ? document.activeElement : null;
        if (typeof host.moveBefore === "function") host.moveBefore(panel, null);
        else host.appendChild(panel);
        focused?.focus({ preventScroll: true });
        this._panelDetached = true;
    }

    _detachPanelEvents() {
        const panel = this.panelElement();
        if (!(panel instanceof HTMLElement) || !this._panelEventsAttached) {
            return;
        }
        panel.removeEventListener("click", this._handleClick);
        panel.removeEventListener("focusin", this._handleFocusIn);
        panel.removeEventListener("focusout", this._handleFocusOut);
        panel.removeEventListener("input", this._handleInput);
        panel.removeEventListener("keydown", this._handleKeyDown);
        panel.removeEventListener("mousedown", this._handleMouseDown);
        this._panelEventsAttached = false;
    }

    _detachResultsEvents() {
        if (!(this._resultsScrollHost instanceof HTMLElement)) {
            this._resultsElement = null;
            this._resultsScrollHost = null;
            return;
        }
        this._resultsScrollHost.removeEventListener("scroll", this._handleResultsScroll);
        this._resultsElement = null;
        this._resultsScrollHost = null;
    }

    _detachViewportListeners() {
        if (!this._viewportListenersAttached) {
            return;
        }
        window.removeEventListener("resize", this._handleViewportChange);
        window.removeEventListener("scroll", this._handleViewportChange, true);
        window.visualViewport?.removeEventListener?.("resize", this._handleViewportChange);
        window.visualViewport?.removeEventListener?.("scroll", this._handleViewportChange);
        this._viewportListenersAttached = false;
        if (this._panelPositionFrame) {
            window.cancelAnimationFrame?.(this._panelPositionFrame);
            this._panelPositionFrame = 0;
        }
    }

    _ensurePanelReference() {
        const panel = reconcilePickerPanel(this, this._panelElement, this._panelDetached);
        if (panel !== this._panelElement) {
            this._detachResultsEvents();
            this._panelElement = panel;
            this._observeResultsMorphTargets();
        }
        if (
            this._panelElement instanceof HTMLElement
            && !this._panelElement.hasAttribute("data-searchable-dropdown-panel")
        ) {
            this._panelElement.setAttribute("data-searchable-dropdown-panel", "");
        }
        // A cohesive morph can move the keyed popup back into its declarative
        // position. Restore the component-owned overlay before it is painted.
        if (this._panelDetached && this.contains(this._panelElement)) {
            this._detachPanel(this._panelElement);
        }
        return this._panelElement;
    }

    _findCatalogTemplateByValue(value) {
        const catalog = this.selectedContentCatalogElement();
        if (!(catalog instanceof HTMLElement)) {
            return null;
        }
        return Array.from(
            catalog.querySelectorAll('template[data-role="selected-content"]'),
        ).find((template) => template.getAttribute("data-value") === value) ?? null;
    }

    _findCatalogOptionContentTemplateByValue(value) {
        const catalog = this.selectedContentCatalogElement();
        if (!(catalog instanceof HTMLElement)) {
            return null;
        }
        return Array.from(
            catalog.querySelectorAll('template[data-role="option-content"]'),
        ).find((template) => template.getAttribute("data-value") === value) ?? null;
    }

    _findOptionByValue(value) {
        return Array.from(
            this.panelElement()?.querySelectorAll?.("[data-searchable-dropdown-option]") ?? [],
        ).find((option) => option.getAttribute("data-value") === value) ?? null;
    }

    _catalogTemplates() {
        const catalog = this.selectedContentCatalogElement();
        if (!(catalog instanceof HTMLElement)) {
            return [];
        }
        return Array.from(catalog.querySelectorAll('template[data-role="selected-content"]'));
    }

    _buildSearchKey(query, selectedValue, excludedValues) {
        return `${selectedValue}\n${Array.from(excludedValues).sort().join("\n")}\n${query}`;
    }

    _hydrateRenderedRemoteResults() {
        const results = this.resultsElement();
        if (
            !(results instanceof HTMLElement)
            || !this._buildSearchUrl("")
            || !results.firstElementChild
        ) {
            return;
        }

        const selectedValue = this.value;
        const excludedValues = this._excludedSelectedInputValues(selectedValue);
        this._lastSearchKey = this._buildSearchKey("", selectedValue, excludedValues);
        this._activeSearchMode = "remote";
        this._activeSearchQuery = "";
        this._activeSearchSelectedValue = selectedValue;
        this._syncResultsPagination();
    }

    _handleClick(event) {
        if (!(event.target instanceof Element)) {
            return;
        }

        const trigger = event.target.closest('[data-role="trigger"]');
        if (trigger && this._ownsNode(trigger)) {
            event.preventDefault();
            this.toggle();
            return;
        }

        const option = event.target.closest("[data-searchable-dropdown-option]");
        if (!option || !this._ownsNode(option)) {
            return;
        }

        event.preventDefault();
        const value = String(option.getAttribute("data-value") ?? "");
        const label = String(option.getAttribute("data-label") ?? option.textContent ?? "").trim();
        const selectionPayload = parseSelectionPayload(option);
        this._syncSelectedContentFromOption(option, label);
        this.select(value, label, selectionPayload);
    }

    _handleDocumentPointerDown(event) {
        if (!(event.target instanceof Node) || this._ownsNode(event.target)) {
            return;
        }
        this.close();
    }

    _handleFocusIn(event) {
        if (event.target instanceof Node && this._ownsNode(event.target)) {
            this._cancelClose();
        }
    }

    _handleFocusOut(event) {
        if (!this.isOpen()) {
            return;
        }

        const nextTarget = event.relatedTarget;
        if (nextTarget instanceof Node && this._ownsNode(nextTarget)) {
            return;
        }

        this._scheduleClose();
    }

    _handleInput(event) {
        if (event.target !== this.searchInputElement()) {
            return;
        }
        this.search(event.target.value);
    }

    _handleKeyDown(event) {
        if (!(event.target instanceof Node) || event.key !== "Escape" || !this._ownsNode(event.target) || !this.isOpen()) {
            return;
        }

        event.preventDefault();
        this.close();
    }

    _handleMouseDown(event) {
        if (!(event.target instanceof Element) || !this._ownsNode(event.target)) {
            return;
        }

        this._cancelClose();

        const option = event.target.closest("[data-searchable-dropdown-option]");
        if (option && this._ownsNode(option)) {
            event.preventDefault();
        }
    }

    _handleResultsScroll() {
        this._maybeLoadMore();
    }

    _handleViewportChange(event = null) {
        if (event?.type === "scroll" && event.target instanceof Node) {
            const panel = this.panelElement();
            if (panel instanceof Node && panel.contains(event.target)) {
                return;
            }
        }
        if (!this.isOpen() || this._panelPositionFrame) {
            return;
        }
        this._panelPositionFrame = window.requestAnimationFrame(() => {
            this._panelPositionFrame = 0;
            this._positionPanel();
        }) || 0;
    }

    _positionOpenDetachedPanel() {
        if (!this.isOpen() || !this._usesDetachedPanel()) {
            return;
        }
        this._positionPanel();
    }

    _scheduleClose() {
        this._cancelClose();
        this._closeTimer = window.setTimeout(() => {
            this._closeTimer = 0;
            this.close();
        }, CLOSE_DELAY_MS);
    }

    _setExpanded(isExpanded) {
        const trigger = this.triggerElement();
        if (trigger instanceof HTMLElement) {
            trigger.setAttribute("aria-expanded", isExpanded ? "true" : "false");
        }
    }

    _localTemplateMatches(template, normalizedQuery) {
        if (!normalizedQuery) {
            return true;
        }
        const haystack = normalizeSearchText(
            [
                template.getAttribute("data-search-text"),
                template.getAttribute("data-label"),
                template.textContent,
            ]
                .filter(Boolean)
                .join(" "),
        );
        return normalizedQuery
            .split(/\s+/)
            .filter(Boolean)
            .every((part) => haystack.includes(part));
    }

    _excludedSelectedInputValues(selectedValue) {
        const selector = this.excludeSelectedInputs;
        if (!selector) {
            return new Set();
        }

        let inputs = [];
        try {
            inputs = Array.from(document.querySelectorAll(selector) ?? []);
        } catch (_) {
            return new Set();
        }

        const normalizedSelectedValue = String(selectedValue ?? "").trim();
        return new Set(
            inputs
                .filter((input) => input !== this)
                .map((input) => String(input?.value ?? "").trim())
                .filter((value) => value && value !== normalizedSelectedValue),
        );
    }

    _filterExcludedRemoteOptions(results, selectedValue) {
        if (!(results instanceof HTMLElement)) {
            return;
        }

        const normalizedSelectedValue = String(selectedValue ?? "").trim();
        const excludedValues = this._excludedSelectedInputValues(normalizedSelectedValue);
        if (!excludedValues.size) {
            return;
        }

        for (const option of results.querySelectorAll("[data-searchable-dropdown-option]")) {
            const value = String(option.getAttribute("data-value") ?? "").trim();
            if (!value || value === normalizedSelectedValue || !excludedValues.has(value)) {
                continue;
            }
            const row = option.closest("li") ?? option;
            row.parentNode?.removeChild(row);
        }
    }

    _localTemplateExcluded(template, selectedValue, excludedValues) {
        const value = String(template.getAttribute("data-value") ?? "").trim();
        return value !== String(selectedValue ?? "").trim() && excludedValues.has(value);
    }

    _centerSelectedResult(results, selectedValue) {
        if (!(results instanceof HTMLElement) || !String(selectedValue ?? "").trim()) {
            return;
        }
        const selected = Array.from(
            results.querySelectorAll("[data-searchable-dropdown-option]"),
        ).find((option) => String(option.getAttribute("data-value") ?? "") === selectedValue);
        const scrollHost = this._resolveResultsScrollHost(results);
        if (!(selected instanceof HTMLElement) || !(scrollHost instanceof HTMLElement)) {
            return;
        }
        const metrics = scrollMetrics(scrollHost);
        if (!hasScrollableRange(metrics)) {
            return;
        }
        const hostRect = scrollHost.getBoundingClientRect();
        const selectedRect = selected.getBoundingClientRect();
        const viewportHeight = Math.max(0, Number(scrollHost.clientHeight || hostRect.height || 0));
        const viewportWidth = Math.max(0, Number(scrollHost.clientWidth || hostRect.width || 0));
        const selectedHeight = Math.max(0, Number(selectedRect.height || 0));
        const selectedWidth = Math.max(0, Number(selectedRect.width || 0));
        const centeredTop = metrics.scrollTop
            + selectedRect.top
            - hostRect.top
            - Math.max(0, (viewportHeight - selectedHeight) / 2);
        const centeredLeft = metrics.scrollLeft
            + selectedRect.left
            - hostRect.left
            - Math.max(0, (viewportWidth - selectedWidth) / 2);
        if (metrics.maxScrollTop > 0) {
            scrollHost.scrollTop = Math.min(metrics.maxScrollTop, Math.max(0, centeredTop));
        }
        if (metrics.maxScrollLeft > 0) {
            scrollHost.scrollLeft = Math.min(metrics.maxScrollLeft, Math.max(0, centeredLeft));
        }
    }

    _renderLocalResults(rawQuery, selectedValue, excludedValues = null) {
        const results = this.resultsElement();
        if (!(results instanceof HTMLElement)) {
            return;
        }

        const templates = this._catalogTemplates();
        const customOption = this._buildCustomOption(rawQuery, selectedValue, templates);
        if (!templates.length) {
            results.replaceChildren(...(customOption ? [customOption] : []));
            this._setResultsNextOffset(results, null);
            this._positionOpenDetachedPanel();
            return;
        }

        const normalizedQuery = normalizeSearchText(rawQuery);
        const excluded = excludedValues instanceof Set
            ? excludedValues
            : this._excludedSelectedInputValues(selectedValue);
        const matches = templates
            .filter((template) => !this._localTemplateExcluded(template, selectedValue, excluded))
            .filter((template) => this._localTemplateMatches(template, normalizedQuery))
            .sort((left, right) => {
                const leftValue = String(left.getAttribute("data-value") ?? "");
                const rightValue = String(right.getAttribute("data-value") ?? "");
                const leftSelected = leftValue === selectedValue ? 0 : 1;
                const rightSelected = rightValue === selectedValue ? 0 : 1;
                return leftSelected - rightSelected;
            });
        this._localSearchState = {
            matches,
            nextOffset: 0,
            selectedValue,
        };

        if (!matches.length && !customOption) {
            const item = document.createElement("li");
            item.className = "menu-disabled";
            const label = document.createElement("span");
            label.textContent = "No matching options";
            item.append(label);
            results.replaceChildren(item);
            this._setResultsNextOffset(results, null);
            this._positionOpenDetachedPanel();
            return;
        }

        results.replaceChildren(...(customOption ? [customOption] : []));
        this._appendLocalResultsPage();
    }

    _appendLocalResultsPage() {
        const results = this.resultsElement();
        const state = this._localSearchState;
        if (!(results instanceof HTMLElement) || !state) {
            return;
        }

        const start = state.nextOffset;
        const end = Math.min(start + RESULTS_PAGE_SIZE, state.matches.length);
        const pageMatches = state.matches.slice(start, end);
        state.nextOffset = end;

        results.append(
            ...pageMatches.map((template) => this._buildLocalResultItem(template, state.selectedValue)),
        );

        const nextOffset = end < state.matches.length ? end : null;
        this._setResultsNextOffset(results, nextOffset);
        this._syncResultsPagination();
        this._scheduleAutoFillIfNeeded();
        this._positionOpenDetachedPanel();
    }

    _buildLocalResultItem(template, selectedValue) {
        const value = String(template.getAttribute("data-value") ?? "");
        const label = String(template.getAttribute("data-label") ?? value).trim();
        const item = document.createElement("li");
        const button = document.createElement("button");
        button.type = "button";
        button.className = `justify-between gap-3 text-left${value === selectedValue ? " menu-active" : ""}`;
        button.dataset.searchableDropdownOption = "";
        button.setAttribute("data-value", value);
        button.setAttribute("data-label", label);

        const optionContent = document.createElement("span");
        optionContent.dataset.role = "option-content";
        optionContent.className = "flex min-w-0 flex-1 items-center gap-3";
        const optionTemplate = this._findCatalogOptionContentTemplateByValue(value);
        optionContent.replaceChildren(
            ...cloneChildNodes(
                optionTemplate instanceof HTMLTemplateElement ? optionTemplate.content : template.content,
            ),
        );
        button.append(optionContent, template.cloneNode(true));

        if (value === selectedValue) {
            const badge = document.createElement("span");
            badge.className = "badge badge-soft badge-primary badge-xs";
            badge.textContent = "Selected";
            button.append(badge);
        }

        item.append(button);
        return item;
    }

    _loadMore() {
        if (this._searchController || this._loadMoreController) {
            return;
        }
        if (this._activeSearchMode === "local") {
            if (!this._localSearchState || this._resultsNextOffset() === null) {
                return;
            }
            this._appendLocalResultsPage();
            return;
        }
        if (this._activeSearchMode === "remote") {
            this._loadMoreRemote();
        }
    }

    _loadMoreRemote() {
        const offset = this._resultsNextOffset();
        if (offset === null) {
            return;
        }

        const searchUrl = this._buildSearchUrl(this._activeSearchQuery, offset);
        if (!searchUrl) {
            return;
        }

        const results = this.resultsElement();
        if (results instanceof HTMLElement) {
            results.setAttribute("aria-busy", "true");
        }

        const controller = new AbortController();
        this._loadMoreController = controller;
        fetch(searchUrl, {
            cache: "no-store",
            headers: {
                Accept: "text/html",
            },
            signal: controller.signal,
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Search request failed: ${response.status}`);
                }
                return response.text();
            })
            .then((html) => {
                if (controller.signal.aborted) {
                    return;
                }

                const currentResults = this.resultsElement();
                if (!(currentResults instanceof HTMLElement)) {
                    return;
                }

                const fragment = parseHtmlFragment(html);
                const nextResults = fragment.firstElementChild;
                if (!(nextResults instanceof HTMLElement)) {
                    return;
                }

                const selectedValue = this.value;
                this._filterExcludedRemoteOptions(nextResults, selectedValue);
                currentResults.append(...Array.from(nextResults.childNodes));
                this._setResultsNextOffset(currentResults, this._resultsNextOffset(nextResults));
                this._syncResultsPagination();
                this._positionOpenDetachedPanel();
            })
            .catch((error) => {
                if (error?.name === "AbortError") {
                    return;
                }
                console.error("Error loading more searchable dropdown results:", error);
            })
            .finally(() => {
                if (results instanceof HTMLElement) {
                    results.removeAttribute("aria-busy");
                }
                if (this._loadMoreController === controller) {
                    this._loadMoreController = null;
                    this._scheduleAutoFillIfNeeded();
                }
            });
    }

    _scheduleAutoFillIfNeeded() {
        if (
            !this._lastSearchKey
            || this._autoFilledSearchKey === this._lastSearchKey
            || this._pendingAutoFillSearchKey === this._lastSearchKey
        ) {
            return;
        }
        this._pendingAutoFillSearchKey = this._lastSearchKey;
        queueMicrotask(() => {
            if (this._pendingAutoFillSearchKey !== this._lastSearchKey) {
                return;
            }
            this._pendingAutoFillSearchKey = "";
            this._maybeAutoFillUntilScrollable();
        });
    }

    _maybeAutoFillUntilScrollable() {
        const results = this.resultsElement();
        if (!(results instanceof HTMLElement) || this._searchController || this._loadMoreController) {
            return;
        }
        if (this._resultsNextOffset(results) === null) {
            return;
        }
        const scrollHost = this._resultsScrollHost ?? this._resolveResultsScrollHost(results);
        if (!(scrollHost instanceof HTMLElement)) {
            return;
        }
        if (hasScrollableRange(scrollMetrics(scrollHost))) {
            return;
        }
        this._autoFilledSearchKey = this._lastSearchKey;
        this._loadMore();
    }

    _syncResultsPagination() {
        this._attachResultsEvents();
    }

    _maybeLoadMore() {
        const results = this.resultsElement();
        if (!(results instanceof HTMLElement) || this._searchController || this._loadMoreController) {
            return;
        }
        if (this._resultsNextOffset(results) === null) {
            return;
        }

        const scrollHost = this._resultsScrollHost ?? this._resolveResultsScrollHost(results);
        if (!(scrollHost instanceof HTMLElement)) {
            return;
        }
        const metrics = scrollMetrics(scrollHost);
        if (!hasScrollableRange(metrics)) {
            return;
        }
        if (isNearScrollEnd(metrics)) {
            this._loadMore();
        }
    }

    _resultsNextOffset(results = this.resultsElement()) {
        const raw = String(results?.getAttribute?.("data-next-offset") ?? "").trim();
        if (!raw) {
            return null;
        }
        const value = Number.parseInt(raw, 10);
        return Number.isInteger(value) && value >= 0 ? value : null;
    }

    _setResultsNextOffset(results, nextOffset) {
        if (!(results instanceof HTMLElement)) {
            return;
        }
        if (Number.isInteger(nextOffset) && nextOffset >= 0) {
            results.setAttribute("data-next-offset", String(nextOffset));
            return;
        }
        results.removeAttribute("data-next-offset");
    }

    _measurePanelRect() {
        const panel = this.panelElement();
        if (!(panel instanceof HTMLElement)) {
            return { width: 0, height: 0 };
        }
        const previousHidden = panel.hidden;
        const previousVisibility = panel.style.visibility;
        const previousWidth = panel.style.width;
        const previousMinWidth = panel.style.minWidth;
        const previousMaxWidth = panel.style.maxWidth;
        const previousMaxHeight = panel.style.maxHeight;
        const previousOverscrollBehavior = panel.style.overscrollBehavior;
        const results = this.resultsElement();
        const previousResultsMaxHeight = results instanceof HTMLElement
            ? results.style.maxHeight
            : "";
        const previousResultsOverflowY = results instanceof HTMLElement
            ? results.style.overflowY
            : "";
        const previousResultsOverscrollBehavior = results instanceof HTMLElement
            ? results.style.overscrollBehavior
            : "";
        const configuredWidth = this._configuredPanelWidthStyle();
        panel.hidden = false;
        panel.style.visibility = "hidden";
        panel.style.maxHeight = "";
        panel.style.overscrollBehavior = "";
        if (results instanceof HTMLElement) {
            results.style.maxHeight = "";
            results.style.overflowY = "";
            results.style.overscrollBehavior = "";
        }
        if (configuredWidth) {
            panel.style.width = configuredWidth;
            panel.style.minWidth = "0";
            panel.style.maxWidth = this._usesOverlayAnchorPlacement()
                ? "100vw"
                : "calc(100vw - 24px)";
        }
        const rect = panel.getBoundingClientRect();
        panel.hidden = previousHidden;
        panel.style.visibility = previousVisibility;
        panel.style.width = previousWidth;
        panel.style.minWidth = previousMinWidth;
        panel.style.maxWidth = previousMaxWidth;
        panel.style.maxHeight = previousMaxHeight;
        panel.style.overscrollBehavior = previousOverscrollBehavior;
        if (results instanceof HTMLElement) {
            results.style.maxHeight = previousResultsMaxHeight;
            results.style.overflowY = previousResultsOverflowY;
            results.style.overscrollBehavior = previousResultsOverscrollBehavior;
        }
        return rect;
    }

    _resolvePanelAnchorElement() {
        const selector = getStringAttribute(this, "panel-anchor-closest");
        if (selector) {
            try {
                const anchor = this.closest(selector);
                if (anchor instanceof HTMLElement) {
                    return anchor;
                }
            } catch (_) {
                // Ignore invalid selectors and fall back to the trigger element.
            }
        }
        return this.triggerElement();
    }

    _usesDetachedPanel() {
        return getStringAttribute(this, "panel-mode") === "detached";
    }

    _usesOverlayAnchorPlacement() {
        return getStringAttribute(this, "panel-placement") === "overlay-anchor";
    }

    _constrainPanelHeight(panel, maxHeight) {
        const constrainedHeight = Math.floor(Number(maxHeight) || 0);
        if (!(panel instanceof HTMLElement) || constrainedHeight <= 0) {
            return;
        }

        const results = this.resultsElement();
        const panelRect = panel.getBoundingClientRect();
        if (results instanceof HTMLElement) {
            const resultsRect = results.getBoundingClientRect();
            const reservedHeight = Math.max(
                0,
                Math.round((panelRect.height || 0) - (resultsRect.height || 0)),
            );
            const resultsMaxHeight = Math.max(48, constrainedHeight - reservedHeight);
            results.style.maxHeight = `${resultsMaxHeight}px`;
            results.style.overflowY = "auto";
            results.style.overscrollBehavior = "contain";
        }

        panel.style.maxHeight = `${constrainedHeight}px`;
        panel.style.overscrollBehavior = "contain";
    }

    _ownsNode(node) {
        if (!(node instanceof Node)) {
            return false;
        }
        const panel = this.panelElement();
        return this.contains(node) || (panel instanceof Node && panel.contains(node));
    }

    _positionPanel(measuredWidth = 0) {
        const panel = this.panelElement();
        const anchor = this._resolvePanelAnchorElement();
        if (!(panel instanceof HTMLElement) || !(anchor instanceof HTMLElement)) {
            return;
        }
        const viewportWidth = Math.max(window.innerWidth || 0, 320);
        const viewportHeight = Math.max(window.innerHeight || 0, 240);
        const visualViewportLeft = window.visualViewport
            ? Math.max(0, window.visualViewport.offsetLeft || 0)
            : 0;
        const visualViewportTop = window.visualViewport
            ? Math.max(0, window.visualViewport.offsetTop || 0)
            : 0;
        const visibleViewportRight = Math.max(
            320,
            Math.min(
                window.innerWidth || Number.POSITIVE_INFINITY,
                document.documentElement.clientWidth || Number.POSITIVE_INFINITY,
                window.visualViewport
                    ? window.visualViewport.offsetLeft + window.visualViewport.width
                    : Number.POSITIVE_INFINITY,
            ),
        );
        const visibleViewportBottom = Math.max(
            240,
            Math.min(
                viewportHeight,
                document.documentElement.clientHeight || Number.POSITIVE_INFINITY,
                window.visualViewport
                    ? window.visualViewport.offsetTop + window.visualViewport.height
                    : Number.POSITIVE_INFINITY,
            ),
        );
        const anchorRect = anchor.getBoundingClientRect();
        const panelWidth = getStringAttribute(this, "panel-width") === "max-content"
            ? Math.ceil(this._measurePanelRect().width)
            : Math.round(measuredWidth || panel.getBoundingClientRect().width || 0);
        const anchorWidth = Math.round(anchorRect.width || 0);
        const overlayAnchor = this._usesOverlayAnchorPlacement();
        const edgeInset = overlayAnchor ? 0 : 12;
        const widthSource = getStringAttribute(this, "panel-min-width");
        const maxWidth = overlayAnchor
            ? visibleViewportRight - edgeInset - anchorRect.left
            : visibleViewportRight - visualViewportLeft - edgeInset * 2;
        const width = Math.max(
            0,
            Math.min(
                widthSource === "panel" ? Math.max(anchorWidth, panelWidth) : (anchorWidth || panelWidth),
                Math.max(0, maxWidth),
            ),
        );
        this._clearDetachedResultsStyles();

        panel.style.position = overlayAnchor ? "absolute" : "fixed";
        panel.style.margin = "0";
        panel.style.zIndex = "70";
        panel.style.width = width ? `${width}px` : "";
        panel.style.minWidth = "0";
        panel.style.maxWidth = `${Math.max(
            visibleViewportRight - visualViewportLeft - edgeInset * 2,
            160,
        )}px`;
        panel.style.maxHeight = "";
        panel.style.overscrollBehavior = "";
        panel.style.left = `${visualViewportLeft + edgeInset}px`;
        panel.style.top = `${visualViewportTop + edgeInset}px`;

        const panelRect = panel.getBoundingClientRect();
        // Convert viewport coordinates through the panel's actual containing block.
        // A dialog-hosted absolute panel does not share the document's origin.
        const originLeft = panelRect.left - (visualViewportLeft + edgeInset);
        const originTop = panelRect.top - (visualViewportTop + edgeInset);
        if (overlayAnchor) {
            this._constrainPanelHeight(
                panel,
                Math.min(panelRect.height, visibleViewportBottom - visualViewportTop),
            );
            const top = Math.max(
                visualViewportTop,
                Math.min(anchorRect.top, visibleViewportBottom - panel.getBoundingClientRect().height),
            );
            // Keep the overlay on its anchor unless viewport bounds require shifting it.
            panel.style.left = `${anchorRect.left - originLeft}px`;
            panel.style.top = `${top - originTop}px`;
            return;
        }

        const minLeft = visualViewportLeft + edgeInset;
        let left = Math.max(minLeft, anchorRect.left);
        if (left + panelRect.width > visibleViewportRight - edgeInset) {
            left = Math.max(
                minLeft,
                visibleViewportRight - panelRect.width - edgeInset,
            );
        }

        const viewportBottom = Math.max(
            visualViewportTop + edgeInset + 1,
            visibleViewportBottom - edgeInset,
        );
        const minTop = visualViewportTop + edgeInset;
        const gap = 8;
        const belowTop = Math.round(anchorRect.bottom + 8);
        const aboveBottom = Math.min(viewportBottom, anchorRect.top - gap);
        const naturalHeight = Math.round(panelRect.height || 0);
        const spaceBelow = Math.max(0, viewportBottom - belowTop);
        const spaceAbove = Math.max(0, aboveBottom - minTop);
        const placeBelow =
            spaceBelow >= naturalHeight
            || (spaceAbove < naturalHeight && spaceBelow >= spaceAbove);
        let topInViewport;
        let availableHeight;
        if (placeBelow) {
            topInViewport = Math.max(minTop, belowTop);
            availableHeight = Math.max(0, viewportBottom - topInViewport);
        } else {
            availableHeight = spaceAbove;
            const panelHeight = naturalHeight
                ? Math.min(naturalHeight, availableHeight)
                : availableHeight;
            topInViewport = Math.max(minTop, Math.round(aboveBottom - panelHeight));
        }

        panel.style.left = `${left - originLeft}px`;
        panel.style.top = `${topInViewport - originTop}px`;
        this._constrainPanelHeight(
            panel,
            Math.min(naturalHeight || availableHeight, availableHeight),
        );
    }

    _restorePanel() {
        const panel = this.panelElement();
        if (!(panel instanceof HTMLElement) || !this._panelDetached) {
            return;
        }
        this._detachPanelEvents();
        this._clearDetachedPanelStyles();
        if (this._panelAnchor?.parentNode) {
            this._panelAnchor.parentNode.insertBefore(panel, this._panelAnchor.nextSibling);
        } else {
            this.appendChild(panel);
        }
        this._panelDetached = false;
    }

    _syncFromValue(rawValue, {
        resolveMissingRemote = false,
        valueChanged = false,
    } = {}) {
        const value = String(rawValue ?? "");
        if (valueChanged) {
            this._abortSelectionHydration();
        }

        const prepared = this._preparedValuePresentation;
        if (prepared?.value === value) {
            this._preparedValuePresentation = null;
            this._applySelectedPresentation(prepared.presentation);
            return;
        }

        const option = this._findOptionByValue(value);
        if (option instanceof HTMLElement) {
            const label = String(
                option.getAttribute("data-label") ?? option.textContent ?? "",
            ).trim();
            if (label) {
                this.label = label;
            }
            this._syncSelectedContentFromOption(option, label);
            this._rememberSelectedPresentation(value);
            return;
        }

        const template = this._findCatalogTemplateByValue(value);
        if (template instanceof HTMLTemplateElement) {
            const label = String(template.getAttribute("data-label") ?? value).trim();
            if (label) {
                this.label = label;
            }
            const container = this.selectedContentElement();
            if (container instanceof HTMLElement) {
                container.replaceChildren(...cloneChildNodes(template.content));
            }
            this._rememberSelectedPresentation(value);
            return;
        }

        const cached = this._selectedPresentationCache.get(value);
        if (cached) {
            this._applySelectedPresentation(cached);
            return;
        }

        const customIsoDate = normalizeIsoDateValue(value);
        if (customIsoDate) {
            this.label = customIsoDate;
            const container = this.selectedContentElement();
            if (container instanceof HTMLElement) {
                const text = document.createElement("span");
                text.className = "truncate font-medium";
                text.textContent = customIsoDate;
                container.replaceChildren(text);
            }
            this._rememberSelectedPresentation(value);
            return;
        }

        if (getStringAttribute(this, "custom-option-mode") === "trade-distance-bonus") {
            const customDistance = normalizeTradeDistanceBonusCustomIdentity(value);
            if (customDistance) {
                this.label = this._tradeDistanceBonusCustomLabels(customDistance).label;
                const container = this.selectedContentElement();
                if (container instanceof HTMLElement) {
                    container.replaceChildren(this._buildTradeDistanceBonusCustomContent(customDistance));
                }
                this._rememberSelectedPresentation(value);
                return;
            }
        }

        // A compact model does not necessarily contain the newly selected
        // option while its dependent model request is in flight. Keep the
        // last resolved presentation (including server-rendered icons and
        // labels) until an option or catalog template can replace it. The raw
        // transport value is an identity, not user-facing fallback content.
        if (
            resolveMissingRemote
            && value !== ""
            && this._buildSearchUrl("")
        ) {
            this._hydrateRemoteSelection(value);
        }
    }

    _canResolveValuePresentation(value) {
        return this._findOptionByValue(value) instanceof HTMLElement
            || this._findCatalogTemplateByValue(value) instanceof HTMLTemplateElement
            || this._selectedPresentationCache.has(value)
            || Boolean(normalizeIsoDateValue(value))
            || (
                getStringAttribute(this, "custom-option-mode") === "trade-distance-bonus"
                && Boolean(normalizeTradeDistanceBonusCustomIdentity(value))
            );
    }

    _hydrateRemoteSelection(value) {
        this.prepareValuePresentation(value)
            .catch((error) => {
                if (error?.name !== "AbortError") {
                    console.error("Error resolving searchable dropdown selection:", error);
                }
            });
    }

    _rememberOptionPresentation(value, option) {
        const label = String(
            option.getAttribute("data-label") ?? option.textContent ?? "",
        ).trim();
        const selectedTemplate = option.querySelector('template[data-role="selected-content"]');
        const optionContent = option.querySelector('[data-role="option-content"]');
        const content = selectedTemplate instanceof HTMLTemplateElement
            ? selectedTemplate.content
            : optionContent instanceof HTMLElement
                ? optionContent
                : null;
        const presentation = {
            label,
            nodes: content ? cloneChildNodes(content) : null,
        };
        this._selectedPresentationCache.set(String(value ?? ""), presentation);
        return presentation;
    }

    _applySelectedPresentation(presentation) {
        this.label = presentation.label;
        const container = this.selectedContentElement();
        if (!(container instanceof HTMLElement)) {
            return;
        }
        if (Array.isArray(presentation.nodes)) {
            container.replaceChildren(
                ...presentation.nodes.map((node) => node.cloneNode(true)),
            );
        } else {
            container.textContent = presentation.label;
        }
    }

    _rememberSelectedPresentation(value) {
        const container = this.selectedContentElement();
        if (!(container instanceof HTMLElement) || !container.childNodes.length) {
            return;
        }
        this._selectedPresentationCache.set(String(value ?? ""), {
            label: this.label,
            nodes: cloneChildNodes(container),
        });
    }

    _syncSelectedContentFromOption(option, fallbackLabel) {
        const container = this.selectedContentElement();
        if (!(container instanceof HTMLElement)) {
            const labelNode = this.selectedLabelElement();
            if (labelNode instanceof HTMLElement) {
                labelNode.textContent = fallbackLabel;
            }
            return;
        }

        const selectedTemplate = option.querySelector('template[data-role="selected-content"]');
        if (selectedTemplate instanceof HTMLTemplateElement) {
            container.replaceChildren(...cloneChildNodes(selectedTemplate.content));
            return;
        }

        const optionContent = option.querySelector('[data-role="option-content"]');
        if (optionContent instanceof HTMLElement) {
            container.replaceChildren(...cloneChildNodes(optionContent));
            return;
        }

        container.textContent = fallbackLabel;
    }

    _syncUi() {
        const selectedContent = this.selectedContentElement();
        if (
            selectedContent instanceof HTMLElement
            && !selectedContent.childNodes.length
            && this.hasAttribute("label")
        ) {
            selectedContent.textContent = this.label;
        }

        const labelNode = this.selectedLabelElement();
        if (labelNode instanceof HTMLElement && this.hasAttribute("label")) {
            labelNode.textContent = this.label;
        }

        const searchInput = this.searchInputElement();
        if (searchInput instanceof HTMLInputElement && this.hasAttribute("placeholder")) {
            searchInput.placeholder = this.placeholder;
        }

        this._setExpanded(this.isOpen());
    }
}

export function registerSearchableDropdown() {
    if (window.customElements.get("fishy-searchable-dropdown")) {
        return;
    }
    window.customElements.define(
        "fishy-searchable-dropdown",
        FishySearchableDropdown,
    );
}
